import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import type { AppUsageUserRow, AppUsageUserDetail, UsageWindow, UsageAppScope } from '../../types/appUsage';
import { getAppUsageUserDetail } from '../../services/appUsageApi';
import { fmtNum, fmtChf, fmtDateTime, lastUsedBadgeClass, daysSince } from './format';

interface Props {
    users: AppUsageUserRow[];
    window: UsageWindow;
    app: UsageAppScope;
    /** The scope has AI calls (stats.apps[].hasAi, or all apps). */
    showAi: boolean;
}

type SortKey = 'email' | 'activity' | 'activeDays' | 'appsUsed' | 'pagesUsed' | 'aiCostChf' | 'lastSeenAt';

export function AppUsageUsersTab({ users, window, app, showAi }: Props) {
    const showApps = app === 'all';
    const [sortBy, setSortBy] = useState<SortKey>('activity');
    const [sortAsc, setSortAsc] = useState(false);
    const [selected, setSelected] = useState<string | null>(null);
    const [detail, setDetail] = useState<AppUsageUserDetail | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);

    useEffect(() => {
        setDetail(null);
        setDetailError(null);
        if (!selected) return;
        let cancelled = false;
        getAppUsageUserDetail(selected, window, app)
            .then(d => { if (!cancelled) setDetail(d); })
            .catch(err => { if (!cancelled) setDetailError(err instanceof Error ? err.message : 'Failed to load'); });
        return () => { cancelled = true; };
    }, [selected, window, app]);

    const activity = (u: AppUsageUserRow) => u.views + u.apiCalls;

    const sorted = [...users].sort((a, b) => {
        const dir = sortAsc ? 1 : -1;
        switch (sortBy) {
            case 'email': return a.email.localeCompare(b.email) * dir;
            case 'activity': return (activity(a) - activity(b)) * dir;
            case 'activeDays': return (a.activeDays - b.activeDays) * dir;
            case 'appsUsed': return (a.appsUsed - b.appsUsed) * dir;
            case 'pagesUsed': return (a.pagesUsed - b.pagesUsed) * dir;
            case 'aiCostChf': return ((a.aiCostChf ?? 0) - (b.aiCostChf ?? 0)) * dir;
            case 'lastSeenAt': {
                const av = a.lastSeenAt ? Date.parse(a.lastSeenAt) : 0;
                const bv = b.lastSeenAt ? Date.parse(b.lastSeenAt) : 0;
                return (av - bv) * dir;
            }
        }
    });

    const header = (key: SortKey, label: string, right = false) => (
        <th
            className={`cursor-pointer select-none px-3 py-2 text-xs font-semibold text-gray-500 hover:text-gray-800 ${right ? 'text-right' : 'text-left'}`}
            onClick={() => {
                if (sortBy === key) setSortAsc(!sortAsc);
                else { setSortBy(key); setSortAsc(key === 'email'); }
            }}
        >
            {label}{sortBy === key ? (sortAsc ? ' ▲' : ' ▼') : ''}
        </th>
    );

    return (
        <div className="flex gap-3">
            <div className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${selected ? 'w-[55%]' : 'w-full'}`}>
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            {header('email', 'User')}
                            {header('lastSeenAt', 'Last seen')}
                            {header('activeDays', 'Active days', true)}
                            {header('activity', 'Views + API', true)}
                            {showApps && header('appsUsed', 'Apps', true)}
                            {header('pagesUsed', 'Pages', true)}
                            {showAi && header('aiCostChf', 'AI (CHF)', true)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {sorted.map(u => {
                            const days = daysSince(u.lastSeenAt);
                            return (
                                <tr
                                    key={u.email}
                                    onClick={() => setSelected(selected === u.email ? null : u.email)}
                                    className={`cursor-pointer hover:bg-gray-50 ${selected === u.email ? 'bg-blue-50' : ''}`}
                                >
                                    <td className="truncate px-3 py-2 font-medium text-gray-900">{u.email}</td>
                                    <td className="px-3 py-2">
                                        <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${lastUsedBadgeClass(u.lastSeenAt)}`}>
                                            {u.lastSeenAt ? (days === 0 ? 'today' : `${days}d ago`) : 'Never'}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(u.activeDays)}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmtNum(activity(u))}</td>
                                    {showApps && <td className="px-3 py-2 text-right tabular-nums">{fmtNum(u.appsUsed)}</td>}
                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {fmtNum(u.pagesUsed)}
                                        {u.pagesEntitled !== null && <span className="text-gray-400"> / {fmtNum(u.pagesEntitled)}</span>}
                                    </td>
                                    {showAi && (
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {u.aiCostChf !== null && u.aiCostChf > 0 ? fmtChf(u.aiCostChf) : '—'}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        {sorted.length === 0 && (
                            <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">No users.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selected && (
                <div className="w-[45%] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4" style={{ maxHeight: '70vh' }}>
                    <div className="mb-3 flex items-center justify-between">
                        <h3 className="truncate text-sm font-semibold text-gray-900">{selected}</h3>
                        <button type="button" onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700" aria-label="Close">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {detailError && <p className="text-sm text-red-600">{detailError}</p>}
                    {!detail && !detailError && <p className="text-sm text-gray-500">Loading…</p>}

                    {detail && (
                        <div className="space-y-4">
                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">
                                    Daily activity ({window === 'all' ? 'last 90 days' : `last ${window} days`}) — UTC
                                </h4>
                                <div className="h-40">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={detail.dailySeries}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                            <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} interval="preserveStartEnd" tick={{ fontSize: 11 }} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                            <Tooltip />
                                            <Legend wrapperStyle={{ fontSize: 11 }} />
                                            <Bar dataKey="views" name="Views" stackId="a" fill="#3b82f6" />
                                            <Bar dataKey="apiCalls" name="API calls" stackId="a" fill="#93c5fd" radius={[2, 2, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Pages (window)</h4>
                                <ul className="space-y-0.5 text-sm">
                                    {detail.pages.map(p => (
                                        <li key={`${p.app}|${p.pageUrl}`} className="flex justify-between gap-2">
                                            <span className="truncate text-gray-800">
                                                {p.pageUrl}
                                                {showApps && <span className="ml-1 text-xs text-gray-400">{p.appName}</span>}
                                            </span>
                                            <span className="whitespace-nowrap tabular-nums text-gray-500">
                                                {fmtNum(p.views)} views · {fmtNum(p.apiCalls)} calls
                                            </span>
                                        </li>
                                    ))}
                                    {detail.pages.length === 0 && <li className="text-gray-500">No activity in window.</li>}
                                </ul>
                            </div>

                            {detail.aiFeatures.length > 0 && (
                                <div>
                                    <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">AI usage (window)</h4>
                                    <ul className="space-y-0.5 text-xs">
                                        {detail.aiFeatures.map(f => (
                                            <li key={f.feature} className="flex justify-between gap-2">
                                                <span className="truncate font-mono text-gray-700">{f.feature}</span>
                                                <span className="whitespace-nowrap tabular-nums text-gray-500">
                                                    {fmtNum(f.calls)} calls · CHF {fmtChf(f.costChf)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div>
                                <h4 className="mb-1 text-xs font-semibold uppercase text-gray-500">Recent events (UTC)</h4>
                                <div className="max-h-48 overflow-y-auto">
                                    <table className="min-w-full text-xs">
                                        <tbody className="divide-y divide-gray-100">
                                            {detail.recentEvents.map((e, i) => (
                                                <tr key={i}>
                                                    <td className="whitespace-nowrap py-1 pr-2 text-gray-500">{fmtDateTime(e.at)}</td>
                                                    <td className="py-1 pr-2 text-gray-700">{e.type}</td>
                                                    <td className="truncate py-1 pr-2 text-gray-700">{e.pageUrl ?? ''}</td>
                                                    <td className="truncate py-1 font-mono text-gray-500">{e.endpoint ?? e.feature ?? ''}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
